import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ConstantValues } from 'src/app/models/util/constantValues';
import { TicketService } from 'src/app/services/ticket.service';
import { ITicketPrintViewModel } from '../../models/print/ticketPrintViewModel.interface';
import { TicketManagerUserService } from '../../services/ticketManagerUser.service';
import { ErrorLevel, ErrorType, EventMappingType, SiteViewConfiguration } from 'src/app/models/enums';
import { NotificationService } from 'src/app/services/notification.service';
import { ICustomNotification } from 'src/app/models/notification/customNotification.interface';
import { DynamicTranslations } from 'src/locale/dynamicTranslations';
import { IUserTM } from '../../models/user/userTM.interface';
import { IAffiliate } from '../../models/user/affiliate.interface';
import { AuthenticationService } from '../../services/authentication.service';
import { IShop } from '../../models/shop.interface';
import { environment } from 'src/environments/environment';
import { ITicketPrintValidationResult, validateTicketPrintModel } from '../../helpers/ticket-print-validation';
import { IPlayedTicket } from 'src/app/models/ticket/playedTicket';
import { IPlayedTicketEvent } from 'src/app/models/ticket/playedTicketEvent';
import { ITicketItemGroupPrintViewModel } from '../../models/print/ITicketItemGroupPrint.interface';

interface IPrintJob {
  ticket: ITicketPrintViewModel;
  fastTicketCode: string;
}

@Component({
  selector: 'app-ticket-print',
  templateUrl: './ticket-print.component.html',
  styleUrls: ['./ticket-print.component.less']
})
export class TicketPrintComponent implements OnInit, OnDestroy {

  private subscription: Subscription = new Subscription();
  public ticketPrint: ITicketPrintViewModel;
  public CV = ConstantValues;
  tmUser: IUserTM;
  affiliate: IAffiliate;
  public fastTicketCode: string;
  date: Date;
  shop: IShop;
  // barcode: string;

  eventMappingTypeEnum = EventMappingType;
  dynamicTranslations = DynamicTranslations;
  enviroment = environment;
  siteViewConfigurationEnum = SiteViewConfiguration;

  // Time the view needs to render the slip, barcode included, before the print dialog is opened.
  private readonly renderDelayMs = 500;
  // Used when the browser never fires 'afterprint', which happens with silent kiosk printing.
  private readonly clearFallbackMs = 5000;

  private printQueue: IPrintJob[] = [];
  private isPrinting = false;
  private lastPrintedKey: string;
  private renderTimer: any;
  private clearFallbackTimer: any;
  private readonly afterPrintHandler = () => this.finishPrint();

  constructor(private ticketService: TicketService, private ticketManagerUserService: TicketManagerUserService, private authService: AuthenticationService, private notificationService: NotificationService) {
    this.subscription.add(this.ticketService.ticketPlayed$.subscribe(ticketAndCode => this.enqueuePrintJob(ticketAndCode)));

    this.affiliate = this.authService.loggedAffiliate;
    this.subscription.add(this.ticketManagerUserService.tmUserChangedSource$.subscribe(() => this.tmUser = ticketManagerUserService.user));
    this.subscription.add(this.authService.loggedAffiliateChangedSource$.subscribe(() => this.affiliate = this.authService.loggedAffiliate));
  }

  // The minimum payout is only worth printing when the ticket can pay out less than the maximum below it.
  public get showMinPayout(): boolean {
    const ticket = this.ticketPrint;
    return !!ticket && ticket.possibleMinPayout > 0 && ticket.possibleMinPayout !== ticket.possibleWinWithoutTax;
  }

  ngOnInit(): void {
    this.tmUser = this.ticketManagerUserService.user;
    window.addEventListener('afterprint', this.afterPrintHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('afterprint', this.afterPrintHandler);
    clearTimeout(this.renderTimer);
    clearTimeout(this.clearFallbackTimer);
    this.subscription.unsubscribe();
  }

  // Every played ticket becomes its own print job, so one slip can never be rendered over another one mid print.
  private enqueuePrintJob(ticketAndCode: { ticket: ITicketPrintViewModel; fastTicketCode: string }): void {
    const job: IPrintJob = { ticket: ticketAndCode.ticket, fastTicketCode: ticketAndCode.fastTicketCode };
    const jobKey = this.getJobKey(job);

    if (!job.ticket && !job.fastTicketCode) {
      return;
    }

    if (jobKey && jobKey === this.lastPrintedKey) {
      return;
    }

    // Amounts that do not follow from the stake and the odds are taken from the server before anything is printed.
    if (job.ticket) {
      const validation = validateTicketPrintModel(job.ticket);
      if (!validation.isValid) {
        this.repairFromServer(job, validation);
        return;
      }
    }

    this.queuePrintJob(job);
  }

  private queuePrintJob(job: IPrintJob): void {
    this.printQueue.push(job);
    this.processPrintQueue();
  }

  /**
   * Reads the ticket back from the server and takes over its amounts. The slip is the binding document,
   * so it is printed only once its amounts come from a source that can be verified later.
   */
  private repairFromServer(job: IPrintJob, validation: ITicketPrintValidationResult): void {
    const ticket = job.ticket;

    if (!ticket.ticketId || !ticket.pinCode) {
      this.reportInvalidPrintModel(ticket, validation);
      return;
    }

    this.subscription.add(this.ticketService.searchPlayerTicket(ticket.ticketId, ticket.pinCode.toString()).subscribe(
      playedTicket => {
        if (!playedTicket) {
          this.reportInvalidPrintModel(ticket, validation);
          return;
        }

        const corrections = this.applyServerAmounts(ticket, playedTicket)
          .concat(this.applyServerSelections(ticket, playedTicket));
        this.reportCorrectedPrintModel(ticket, validation, corrections);

        // The amounts are the server's now, so the slip is printed even if the check still disagrees.
        this.queuePrintJob(job);
      },
      () => this.reportInvalidPrintModel(ticket, validation)
    ));
  }

  /**
   * Takes over only the amounts the fetched ticket actually carries. A field the server does not return
   * keeps its current value, and a returned zero is a value like any other.
   */
  private applyServerAmounts(ticket: ITicketPrintViewModel, playedTicket: IPlayedTicket): string[] {
    const corrections: string[] = [];
    const amounts = ticket as unknown as { [field: string]: number };

    const takeOver = (field: string, serverValue: number) => {
      if (serverValue == null) {
        return;
      }

      const currentValue = amounts[field];
      if (currentValue === serverValue) {
        return;
      }

      corrections.push(`${field}: ${currentValue} -> ${serverValue}`);
      amounts[field] = serverValue;
    };

    takeOver('bonus', playedTicket.bonus);
    takeOver('possibleWin', playedTicket.possibleMaxWin);
    takeOver('possibleWinWithoutTax', playedTicket.possiblePayout);
    takeOver('minWinings', playedTicket.possibleMinWin);
    takeOver('possibleMinPayout', playedTicket.possibleMinPayout);
    takeOver('taxOnWinning', playedTicket.taxOnWinning);
    takeOver('taxOnMinWinning', playedTicket.taxOnMinWinning);
    takeOver('taxBeforeStake', playedTicket.taxBeforeStake);
    takeOver('totalOdd', playedTicket.totalOdd);
    takeOver('numOfCombinations', playedTicket.numberOfCombinations);
    takeOver('payin', playedTicket.payin);
    takeOver('totalStake', playedTicket.stake);
    takeOver('systemItemsCount', playedTicket.systemEventsCount);
    takeOver('fixItemsCount', playedTicket.fixEventsCount);

    if (playedTicket.systemEventsInCombination) {
      ticket.systems = playedTicket.systemEventsInCombination;
    }

    return corrections;
  }

  /**
   * Replaces the selections when they do not match the fetched ticket. They are rebuilt as a whole,
   * because a slip showing a different event or odd than the ticket is wrong regardless of which field differs.
   */
  private applyServerSelections(ticket: ITicketPrintViewModel, playedTicket: IPlayedTicket): string[] {
    if (!playedTicket.events || playedTicket.events.length === 0) {
      return [];
    }

    const isTicketFix = playedTicket.system === 'Fix';
    const serverGroups = playedTicket.events.map(event => this.mapServerEventGroup(event, isTicketFix));

    if (this.selectionsSignature(ticket.ticketItemGroups) === this.selectionsSignature(serverGroups)) {
      return [];
    }

    ticket.ticketItemGroups = serverGroups;
    ticket.eventsCount = serverGroups.length;

    return ['ticketItemGroups'];
  }

  private mapServerEventGroup(event: IPlayedTicketEvent, isTicketFix: boolean): ITicketItemGroupPrintViewModel {
    return {
      eventId: event.id,
      sportName: event.sport,
      regionName: event.region,
      competitionName: event.competition,
      eventName: event.name,
      eventCode: event.code,
      eventDateTime: event.dateTime,
      mappingType: event.eventMappingType,
      isInBankersGroup: !(isTicketFix || event.system === 'S'),
      isLive: event.isLive,
      ticketItems: (event.outcomes || []).map(outcome => ({
        sbv: outcome.sbv,
        odd: outcome.odd,
        betTypeName: outcome.betTypeName,
        betTypeOutcomeName: outcome.betTypeOutcomeName,
        isLive: outcome.isLive
      }))
    };
  }

  private selectionsSignature(groups: ITicketItemGroupPrintViewModel[]): string {
    return (groups || [])
      .map(group => {
        const items = (group.ticketItems || [])
          .map(item => `${item.betTypeName}:${item.betTypeOutcomeName}:${item.sbv ?? ''}:${item.odd}`)
          .sort()
          .join(',');

        return `${group.eventId}|${group.eventCode}|${items}`;
      })
      .sort()
      .join(';');
  }

  private reportCorrectedPrintModel(ticket: ITicketPrintViewModel, validation: ITicketPrintValidationResult, corrections: string[]): void {
    console.warn('Ticket print model corrected from the server before printing.', {
      ticketId: ticket.ticketId,
      pinCode: ticket.pinCode,
      failedCheck: validation.check,
      expected: validation.expected,
      actual: validation.actual,
      corrections
    });
  }

  private processPrintQueue(): void {
    if (this.isPrinting || this.printQueue.length === 0) {
      return;
    }

    const job = this.printQueue.shift();
    if (!job) {
      return;
    }

    this.isPrinting = true;
    this.lastPrintedKey = this.getJobKey(job);

    this.ticketPrint = job.ticket;
    this.fastTicketCode = job.fastTicketCode;
    this.date = new Date();
    // this.barcode = +ticket.ticketId + ticket.pinCode.toString();

    this.renderTimer = setTimeout(() => {
      window.print();
      this.clearFallbackTimer = setTimeout(() => this.finishPrint(), this.clearFallbackMs);
    }, this.renderDelayMs);
  }

  // The printed slip stays in the view until the next ticket replaces it, so a reprint still shows the last sale.
  private finishPrint(): void {
    if (!this.isPrinting) {
      return;
    }

    clearTimeout(this.clearFallbackTimer);
    this.isPrinting = false;
    this.processPrintQueue();
  }

  private getJobKey(job: IPrintJob): string {
    return job.ticket ? job.ticket.ticketId : job.fastTicketCode;
  }

  private reportInvalidPrintModel(ticket: ITicketPrintViewModel, validation: ITicketPrintValidationResult): void {
    console.error('Ticket print model is not consistent with the played ticket.', {
      ticketId: ticket.ticketId,
      pinCode: ticket.pinCode,
      failedCheck: validation.check,
      expected: validation.expected,
      actual: validation.actual,
      ticketPrint: ticket
    });

    const notification: ICustomNotification = {
      errorLevel: ErrorLevel.Warning,
      errorType: ErrorType.CloseDialog,
      title: DynamicTranslations.ticketPrint_AmountMismatch_title,
      message: DynamicTranslations.ticketPrint_AmountMismatch_ErrorMessage + ticket.ticketId
    } as ICustomNotification;

    this.notificationService.forwardDataToNotification(notification);
  }

}
